import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SFormControl } from "../../components/BasicComponents";
import Chat, { MessageReply } from "../../components/Chat/Chat";
import Notes from "../../components/Notes/Notes";
import Participants from "../../components/Participants/Participants";
import { SocketContext } from "../../context/SocketContext";
import { StudentContext } from "../../context/StudentContext";
import { MediaContext } from "../../context/MediaContext";
import { Slider } from "../../components/Slider/Slider";
import { BookmarkModal, QuestionModal } from "../../components/Modals/Modals";
import shortLogo from "../../assets/images/evoliShortLogo.png";
import { isLectureLive, isLectureAvailable, isWaitingForTeacher } from "../../utils/lectureUtils";
import Dashboard from "../../components/Dashboard/Dashboard";
import PDFReader from "../../components/PDFReader/PDFReader";
import { Styles, SidePanel, SReactions, Sidenav, FeedbackControls } from "./Lecture.styles";
import WaitingRoom from "../../components/WaitingRoom/WaitingRoom";
import "../../styles/reset.css";
import { toastError } from "../../utils/Toast";
import Loading from "../../components/Loading/Loading";
import LectureVideoPlayer from "../../components/YouTubePlayer/LectureVideoPlayer";
import Questions from "../../components/Questions/Questions";
import FeedbackBar from "../../components/FeedbackBar/FeedbackBar";

const StudentLecturePage = (props) => {
  const { t } = useTranslation("", { keyPrefix: "lecture.video_page" });

  const { connectSocket, disconnectSocket, participants, functionalities, setupGraphUpdate, stopGraphUpdate } =
    useContext(SocketContext);

  const { selectLecture, currentLecture, getSlidesURL, downloadSlides, loading } = useContext(StudentContext);
  const { player } = useContext(MediaContext);

  const history = useHistory();

  const [showFeedback, setShowFeedback] = useState(false);
  const [panel, enablePanel] = useState(false);
  const [selectedTab, setSelectedTab] = useState("chat");
  const [showSecondaryPanel, setShowSecondaryPanel] = useState(true);
  const [videoMainContent, setVideoMainContent] = useState(true);
  const [slideURL, setSlideURL] = useState("");
  const [duration, setDuration] = useState(0);
  const [stopped, setStopped] = useState(false);

  const [replyTo, setReplyTo] = useState(null);

  const youtubeVisible = videoMainContent || (!videoMainContent && showSecondaryPanel);

  useEffect(() => {
    //If the URL is correctly formatted
    if (props.match.path === "/student/lecture/:lectureUUID") {
      const lectureUUID = props.match.params.lectureUUID;

      selectLecture(lectureUUID).then((lecture) => {
        if (lecture) {
          setSlideURL(getSlidesURL(lectureUUID));
          connectSocket(false, lectureUUID);
          setupGraphUpdate();
        } else {
          toastError(t("toast.lecture_error"));
          history.push("/student");
        }
      });

      return () => {
        disconnectSocket();
        stopGraphUpdate();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickMenuItem = (tabName) => {
    if (tabName === selectedTab) enablePanel(!panel);
    else enablePanel(true);
    setSelectedTab(tabName);
    setShowFeedback(false);
  };

  const isSidePanelVisible = () => {
    if (!panel) return false;
    if (!functionalities.chat && selectedTab === "chat") return false;
    if (!functionalities.notes && selectedTab === "notes") return false;
    if (!functionalities.feedback && selectedTab === "feedback") return false;
    return true;
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          {isLectureAvailable(currentLecture) ? (
            <Styles
              panelVisible={isSidePanelVisible()}
              youtubeVisible={youtubeVisible}
              secondaryContent={currentLecture.slides}
            >
              {!isWaitingForTeacher(currentLecture, participants.teacher) ? (
                <div>
                  <LectureVideoPlayer
                    videoId={currentLecture.videoId}
                    mainContent={videoMainContent}
                    setStopped={setStopped}
                    setDuration={setDuration}
                  />

                  {!isLectureLive(currentLecture) && stopped && (
                    <FeedbackBar isSidePanelVisible={isSidePanelVisible()} duration={duration} />
                  )}
                </div>
              ) : (
                <WaitingForTeacherRoom videoMainContent={videoMainContent} sidePanelVisible={isSidePanelVisible()} />
              )}
              {currentLecture.slides && (!videoMainContent || (videoMainContent && showSecondaryPanel)) && (
                <div
                  className={classNames({
                    "main-content": !videoMainContent,
                    "secondary-content": videoMainContent,
                  })}
                >
                  <PDFReader
                    filePath={slideURL}
                    expanded={!videoMainContent}
                    setExpanded={setVideoMainContent}
                    downloadSlides={() => downloadSlides(currentLecture.uuid)}
                  />
                </div>
              )}

              <Dashboard
                lecture={currentLecture}
                sidePanelVisible={isSidePanelVisible()}
                visible={showFeedback && functionalities.feedback}
              />

              <SidePanel visible={isSidePanelVisible()} selected={selectedTab}>
                <Row className="flex-grow-1 content-row">
                  {selectedTab === "chat" && <Chat replyClick={(messageUUID) => setReplyTo(messageUUID)} />}
                  {selectedTab === "notes" && <Notes />}
                  {selectedTab === "participants" && <Participants />}
                  {selectedTab === "questions" && <Questions replyClick={(messageUUID) => setReplyTo(messageUUID)} />}
                </Row>
                <Row>
                  {(selectedTab === "chat" || (selectedTab === "questions" && replyTo)) && (
                    <SendForm replyingTo={replyTo} removeReply={() => setReplyTo(null)} />
                  )}
                </Row>
              </SidePanel>

              <Sidenav expand="md">
                <div className="menu">
                  <div className="logo" onClick={() => history.push("/")}>
                    <img src={shortLogo} alt="EVOLI" />
                    <p className="p-0 m-0 fs-5 lecture-type">{isLectureLive(currentLecture) ? t("live") : t("rec")}</p>
                  </div>
                  {functionalities.chat && isLectureLive(currentLecture) && (
                    <button
                      className={classNames("menu-item", { active: selectedTab === "chat" && panel })}
                      onClick={() => onClickMenuItem("chat")}
                    >
                      <i className="fas fa-comment-dots w-auto" />
                      <span>{t("chat")}</span>
                    </button>
                  )}
                  {!isLectureLive(currentLecture) && (
                    <button
                      className={classNames("menu-item", { active: selectedTab === "questions" && panel })}
                      onClick={() => onClickMenuItem("questions")}
                    >
                      <i className="fas fa-question w-auto" />
                      <span>Questions</span>
                    </button>
                  )}
                  {functionalities.notes && (
                    <button
                      className={classNames("menu-item", { active: selectedTab === "notes" && panel })}
                      onClick={() => onClickMenuItem("notes")}
                    >
                      <i className="fas fa-book w-auto" />
                      <span>{t("notes")}</span>
                    </button>
                  )}
                  <button
                    className={classNames("menu-item", { active: selectedTab === "participants" && panel })}
                    onClick={() => onClickMenuItem("participants")}
                  >
                    <i className="fas fa-users w-auto" />
                    <span>{t("participants")}</span>
                  </button>
                  {functionalities.feedback && (
                    <button
                      className={classNames("menu-item", { active: showFeedback })}
                      onClick={() => {
                        setShowFeedback(!showFeedback);
                        enablePanel(false);
                      }}
                    >
                      <i className="fas fa-chart-bar w-auto" />
                      <span>{t("feedback")}</span>
                    </button>
                  )}
                  {!videoMainContent && !isWaitingForTeacher(currentLecture) && (
                    <button
                      className={classNames("menu-item", { active: showSecondaryPanel })}
                      onClick={() => setShowSecondaryPanel(!showSecondaryPanel)}
                    >
                      <i className="fas fa-video w-auto" />
                      <span>{t("video")}</span>
                    </button>
                  )}
                  {videoMainContent && currentLecture.slides && (
                    <button
                      className={classNames("menu-item slides", { active: showSecondaryPanel })}
                      onClick={() => setShowSecondaryPanel(!showSecondaryPanel)}
                    >
                      <i className="fas fa-file-pdf w-auto" />
                      <span>{t("slides")}</span>
                    </button>
                  )}
                </div>
              </Sidenav>

              {!isWaitingForTeacher(currentLecture, participants.teacher) && (
                <FeedbackControls panelVisible={isSidePanelVisible()}>
                  {isLectureLive(currentLecture) ? (
                    <Slider player={player} />
                  ) : (
                    <AsyncReactions setStopped={setStopped} />
                  )}
                </FeedbackControls>
              )}
            </Styles>
          ) : (
            <WaitingRoom lecture={currentLecture} />
          )}
        </>
      )}
    </>
  );
};

const AsyncReactions = ({ setStopped }) => {
  const { t } = useTranslation("", { keyPrefix: "lecture.video_page" });

  const { sendSocketEvent } = useContext(SocketContext);
  const { getVideoTs } = useContext(MediaContext);
  const [onHoverClear, setOnHoverClear] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);

  const onClickEmoji = (value) => {
    sendSocketEvent("feedback/understanding", { value, videoTs: getVideoTs() });
    //Display reactions for 2 seconds
    setStopped(true);
    setTimeout(setStopped, 2000, false);
  };

  return (
    <SReactions>
      <div className="reactions">
        {!onHoverClear ? (
          <>
            <div className="reaction" onClick={() => setShowBookmarkModal(true)}>
              <i className="fs-1 far fa-bookmark" />
              <div className="description">{t("reactions.new")}</div>
            </div>
            <div className="reaction" onClick={() => setShowQuestionModal(true)}>
              <i className="fs-1 fas fa-question" />
              <div className="description">{t("reactions.ask")}</div>
            </div>
            <div className="reaction" onMouseOver={() => setOnHoverClear(true)}>
              <i className="fs-1 fas fa-lightbulb" />
              <div className="description">{t("reactions.clear_question")}</div>
            </div>
          </>
        ) : (
          <div className="clear" onMouseLeave={() => setOnHoverClear(false)}>
            <div className="reaction">
              <i className="fs-1 fas fa-dizzy" onClick={() => onClickEmoji(1)} />
              <div className="description">{t("reactions.unclear")}</div>
            </div>
            <div className="reaction">
              <i className="fs-1 fas fa-meh" onClick={() => onClickEmoji(2)} />
              <div className="description">{t("reactions.half")}</div>
            </div>
            <div className="reaction">
              <i className="fs-1 fas fa-smile" onClick={() => onClickEmoji(3)} />
              <div className="description">{t("reactions.clear")}</div>
            </div>
          </div>
        )}
      </div>
      <QuestionModal show={showQuestionModal} onHide={() => setShowQuestionModal(false)} />
      <BookmarkModal show={showBookmarkModal} onHide={() => setShowBookmarkModal(false)} />
    </SReactions>
  );
};

const SendForm = ({ replyingTo = null, removeReply }) => {
  const { t } = useTranslation("", { keyPrefix: "components.chat" });
  const [message, setMessage] = useState("");

  const { sendSocketEvent, chatMessages } = useContext(SocketContext);
  const { getVideoTs, slideNumber } = useContext(MediaContext);

  const onSend = (e) => {
    e.preventDefault();

    if (message.length > 0) {
      sendSocketEvent("chat/message", {
        text: message,
        videoTs: getVideoTs(),
        slideNumber: slideNumber,
        parent: replyingTo || undefined,
      });

      const form = document.getElementById("form-chat");
      form.reset();

      removeReply();
    }
  };

  return (
    <Col>
      {replyingTo && (
        <div>
          <button className="fas fa-times position-absolute" onClick={removeReply} />
          <MessageReply message={chatMessages[replyingTo]} />
        </div>
      )}

      <Form id="form-chat" className="m-2" onSubmit={onSend}>
        <Row>
          <InputGroup>
            <SFormControl
              className="p-3 fs-5"
              type="text"
              placeholder={t("send")}
              onChange={(e) => setMessage(e.target.value)}
            />
            <Button type="submit" className="send-button">
              <i className="fas fs-4 p-3 pe-4 fa-paper-plane" />
            </Button>
          </InputGroup>
        </Row>
      </Form>
    </Col>
  );
};

const WaitingForTeacherRoom = ({ videoMainContent }) => {
  const { t } = useTranslation("", { keyPrefix: "lecture.video_page" });

  return (
    <div
      className={classNames("waiting", {
        "main-content": videoMainContent,
        "secondary-content": !videoMainContent,
      })}
    >
      <div className="d-flex flex-column justify-content-center align-items-center h-100 w-100">
        <h1 className="fw-bolder">{t("waiting_title")}</h1>
        <h3>{t("waiting_description")}</h3>
      </div>
    </div>
  );
};

export default StudentLecturePage;
